import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Music, Calendar, MapPin, Radio, Heart, PoundSterling, Clock, Send, X, User, Facebook, Instagram, Twitter, ExternalLink, CreditCard, Loader2, TrendingUp, Search, Users } from 'lucide-react';
import { SongCard } from '@/features/song-requests/components/SongCard';
import { RequestBasketBar } from '@/features/song-requests/components/RequestBasketBar';
import { RequestBasketCheckout } from '@/features/song-requests/components/RequestBasketCheckout';
import A2BPaymentForm from '@/components/a2b/A2BPaymentForm';
import { ysr2Mobile } from '@/features/shared/styles/mobileDesignSystem';

const PayPalUSProcessor = lazy(() => import('@/components/paypal/PayPalUSProcessor.jsx'));
import { useSongRequests } from '@/features/song-requests/hooks/useSongRequests';
import { formatCurrency, DEFAULT_CURRENCY, getCurrencySymbol } from '@/lib/currency';
import { isPayPalCheckoutCountry, isPayPalPaymentProvider } from '@/lib/paypalCheckout';

// ✅ IMPORT FIREBASE SERVICES
import { db } from '@/lib/firebase';
import { collection, query, where, getDocs, doc, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { filterExpiredGigs } from '@/lib/gigUtils';
import { getGoogleStaticMapUrl } from '@/lib/config'; // This helper function is unchanged
import { trackPageView, updateLastSeen, removeActiveViewer, trackSongInteraction, recordLiveSessionPageVisit } from '@/lib/viewerTracking';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import UserNotificationCenter from '@/features/shared/components/UserNotificationCenter';

const ArtistPage = () => {
    const { artistUrlSlug } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [artist, setArtist] = useState(null);
    const [songs, setSongs] = useState([]);
    const [liveStatus, setLiveStatus] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [activePlaylist, setActivePlaylist] = useState(null);
    const [playlists, setPlaylists] = useState([]);
    const [gigs, setGigs] = useState([]);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeLeft, setTimeLeft] = useState('');
    const [tipModalOpen, setTipModalOpen] = useState(false);
    const [latestTip, setLatestTip] = useState(null);
    const [recentTips, setRecentTips] = useState([]);
    const [tipFormData, setTipFormData] = useState({
        name: '',
        amount: '',
        note: ''
    });
    const [isSubmittingTip, setIsSubmittingTip] = useState(false);
    const [showTipPaymentModal, setShowTipPaymentModal] = useState(false);
    const [tipPaymentData, setTipPaymentData] = useState(null);
    const popularSectionRef = useRef(null);
    const requestSectionRef = useRef(null);
    /** "Request a Song" title row — scroll anchor so the full header stays visible under docked tabs. */
    const requestSongHeadingRef = useRef(null);
    const tipJarSectionRef = useRef(null);
    /** When true, skip one restore of `songsCatalogueScrollYRef` (e.g. jump to Popular from Search). */
    const suppressNextSongsScrollRestoreRef = useRef(false);
    /** 'popular' | 'songlist' | null — brief highlight after "Be the first" tap */
    const [pulseTarget, setPulseTarget] = useState(null);
    const [catalogueSearchQuery, setCatalogueSearchQuery] = useState('');
    const [catalogueSearchFocused, setCatalogueSearchFocused] = useState(false);
    /** Public catalogue: Songs (default) | grouped Artists | Search */
    const [catalogueViewTab, setCatalogueViewTab] = useState('songs');
    const catalogueSearchInputRef = useRef(null);
    const catalogueTabsSentinelRef = useRef(null);
    const catalogueTabsBarRef = useRef(null);
    /** Violet search card (always in document flow — fixed search caused Safari overlap/ghosting with page content). */
    const catalogueSearchPanelRef = useRef(null);
    /** Top of grouped Artists list — scroll target when switching to Artists. */
    const catalogueArtistsListRef = useRef(null);
    const prevCatalogueViewTabRef = useRef(catalogueViewTab);
    /** Saved window scroll when leaving Songs tab (restore when returning). */
    const songsCatalogueScrollYRef = useRef(null);
    /** Scroll-driven dock: CSS sticky breaks on many mobile layouts; fixed + placeholder matches “pin to top until scroll back”. */
    const [catalogueTabsDocked, setCatalogueTabsDocked] = useState(false);
    const [catalogueTabsBarHeight, setCatalogueTabsBarHeight] = useState(0);
    const [basketSongs, setBasketSongs] = useState([]);
    const [basketCheckoutOpen, setBasketCheckoutOpen] = useState(false);
    const basketHydratedRef = useRef(false);

    const basketIds = useMemo(() => new Set(basketSongs.map((song) => song.id)), [basketSongs]);
    const basketTotal = useMemo(
        () => basketSongs.reduce((sum, song) => sum + (Number(song.price) || 0), 0),
        [basketSongs]
    );

    const handleToggleBasket = useCallback((song) => {
        if (!song?.id) return;
        const alreadyIn = basketSongs.some((item) => item.id === song.id);
        if (alreadyIn) {
            setBasketSongs((prev) => prev.filter((item) => item.id !== song.id));
            return;
        }
        if (liveStatus?.accepting_new_requests === false) {
            toast({
                title: 'Requests paused',
                description: 'This artist is not accepting new requests right now.',
                variant: 'destructive',
            });
            return;
        }
        setBasketSongs((prev) => {
            if (prev.some((item) => item.id === song.id)) return prev;
            return [
                ...prev,
                {
                    id: song.id,
                    title: song.title,
                    original_artist: song.original_artist || song.artist || 'Unknown Artist',
                    artist: song.artist || null,
                    price: Number(song.price) || 0,
                },
            ];
        });
        if (artist?.id) {
            trackSongInteraction(artist.id, song.id, 'added_to_cart', sessionId);
        }
    }, [artist?.id, basketSongs, liveStatus?.accepting_new_requests, sessionId, toast]);

    const handleBasketPaid = useCallback(() => {
        setBasketSongs([]);
        setBasketCheckoutOpen(false);
        if (artist?.id) {
            try {
                sessionStorage.removeItem(`ysr_request_basket_${artist.id}`);
            } catch {
                // ignore storage errors
            }
        }
    }, [artist?.id]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const pay = params.get('ysr_payment');
        if (pay === 'success' || pay === 'pending') {
            setBasketSongs([]);
            setBasketCheckoutOpen(false);
            basketHydratedRef.current = true;
        }
    }, []);

    useEffect(() => {
        if (liveStatus && liveStatus.is_live === false) {
            basketHydratedRef.current = false;
            setBasketSongs([]);
            setBasketCheckoutOpen(false);
            if (artist?.id) {
                try {
                    sessionStorage.removeItem(`ysr_request_basket_${artist.id}`);
                } catch {
                    // ignore storage errors
                }
            }
        }
    }, [liveStatus?.is_live, artist?.id]);

    useEffect(() => {
        if (!artist?.id || liveStatus?.is_live !== true) return;
        if (!basketHydratedRef.current) {
            basketHydratedRef.current = true;
            const params = new URLSearchParams(window.location.search);
            const pay = params.get('ysr_payment');
            if (pay === 'success' || pay === 'pending') {
                try {
                    sessionStorage.removeItem(`ysr_request_basket_${artist.id}`);
                } catch {
                    // ignore storage errors
                }
                setBasketSongs([]);
                return;
            }
            try {
                const raw = sessionStorage.getItem(`ysr_request_basket_${artist.id}`);
                if (!raw) return;
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setBasketSongs((prev) => (prev.length > 0 ? prev : parsed));
                }
            } catch {
                // ignore storage errors
            }
            return;
        }
        try {
            sessionStorage.setItem(`ysr_request_basket_${artist.id}`, JSON.stringify(basketSongs));
        } catch {
            // ignore storage errors
        }
    }, [artist?.id, liveStatus?.is_live, basketSongs]);

    useEffect(() => {
        if (basketSongs.length === 0) setBasketCheckoutOpen(false);
    }, [basketSongs.length]);
    
    useSongRequests(artist?.id, 'artist', true, sessionId);

    // ✅ Get artist's currency (fallback to GBP for existing UK artists)
    const artistCurrency = useMemo(() => {
        return artist?.currency || DEFAULT_CURRENCY;
    }, [artist?.currency]);

    const usePayPal = isPayPalCheckoutCountry(artist?.country);

    // ✅ Create currency formatter using artist's currency
    const formatArtistCurrency = useCallback((amount) => {
        return formatCurrency(amount, artistCurrency);
    }, [artistCurrency]);

    // Helper function to calculate relative time
    const getRelativeTime = (timestamp) => {
        if (!timestamp) return 'Just now';
        
        const now = new Date();
        const tipTime = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const diffInMinutes = Math.floor((now - tipTime) / (1000 * 60));
        
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes === 1) return '1m ago';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        
        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours === 1) return '1h ago';
        if (diffInHours < 24) return `${diffInHours}h ago`;
        
        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays === 1) return '1d ago';
        return `${diffInDays}d ago`;
    };

    // ✅ REFACTORED: Fetch initial data from Firestore based on URL slug
    useEffect(() => {
        const fetchData = async () => {
            if (!artistUrlSlug) return;
            setLoading(true);
            try {
                // Normalize the URL slug: remove trailing slash, convert to lowercase, trim whitespace
                const normalizedSlug = artistUrlSlug.replace(/\/$/, '').toLowerCase().trim();
                
                // Step 1: Find the artist profile document by their url_slug (case-insensitive)
                const profilesRef = collection(db, "profiles");
                
                // Fast case-insensitive lookup: try lowercase first (most common), then original case
                let querySnapshot = null;
                
                // First try normalized lowercase (most common case for new profiles)
                const lowercaseQ = query(profilesRef, where("url_slug", "==", normalizedSlug));
                querySnapshot = await getDocs(lowercaseQ);
                
                // If not found, try with original case (for backward compatibility)
                if (querySnapshot.empty) {
                    const originalSlug = artistUrlSlug.replace(/\/$/, '').trim();
                    const originalQ = query(profilesRef, where("url_slug", "==", originalSlug));
                    querySnapshot = await getDocs(originalQ);
                }
                
                // If still not found, try case-insensitive search by getting all profiles and filtering
                if (querySnapshot.empty) {
                    const allProfilesSnapshot = await getDocs(profilesRef);
                    const matchingProfile = allProfilesSnapshot.docs.find(doc => {
                        const slug = doc.data().url_slug;
                        return slug && slug.toLowerCase() === normalizedSlug;
                    });
                    
                    if (matchingProfile) {
                        querySnapshot = {
                            docs: [matchingProfile],
                            empty: false
                        };
                    }
                }

                if (querySnapshot.empty || !querySnapshot.docs || querySnapshot.docs.length === 0) {
                    setError(`Artist "${artistUrlSlug}" not found.`);
                    setLoading(false);
                    return;
                }

                const profileData = { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() };
                
                // ✅ CRITICAL: If this is a karaoke account, redirect to karaoke route
                if (profileData.account_type === 'karaoke') {
                    console.log('🔄 Redirecting karaoke account to karaoke route:', profileData.url_slug);
                    navigate(`/karaoke/${profileData.url_slug}`, { replace: true });
                    return;
                }
                
                setArtist(profileData);
                const artistId = profileData.id;
                
                // Track page view
                trackPageView(artistId, profileData.url_slug);

                // Step 2: Fetch all related data using the artist's ID (user.uid)
                // Simplified query to avoid composite index requirement
                const songsQuery = query(collection(db, 'songs'), where('user_id', '==', artistId));
                const songsSnapshot = await getDocs(songsQuery);
                const songsData = songsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                // Filter and sort in memory instead of requiring a composite index
                const activeSongs = songsData
                    .filter(song => !song.archived)
                    .sort((a, b) => (a.order || 0) - (b.order || 0));
                setSongs(activeSongs);

                const gigsQuery = query(collection(db, 'gigs'), where('user_id', '==', artistId));
                const gigsSnapshot = await getDocs(gigsQuery);
                const gigsData = gigsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                // Filter out expired gigs (gigs that have passed after midnight locally)
                const activeGigs = filterExpiredGigs(gigsData);
                
                // Sort in memory instead of requiring a composite index
                const sortedGigs = activeGigs.sort((a, b) => {
                    const dateA = new Date(a.date);
                    const dateB = new Date(b.date);
                    return dateA - dateB; // Ascending order for upcoming gigs
                });
                setGigs(sortedGigs);
                
                const playlistsQuery = query(collection(db, 'playlists'), where('user_id', '==', artistId));
                const playlistsSnapshot = await getDocs(playlistsQuery);
                setPlaylists(playlistsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            } catch (err) {
                console.error("Error fetching artist data:", err);
                
                // Handle specific Firebase errors gracefully
                if (err.code === 'permission-denied' || err.message.includes('Missing or insufficient permissions')) {
                    setError("This artist's profile is not publicly accessible.");
                } else if (err.code === 'not-found') {
                    setError("Artist profile not found.");
                } else {
                    setError("Failed to load artist data. Please try again later.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [artistUrlSlug]);

    // ✅ REFACTORED: Real-time listeners for live_status and requests
    useEffect(() => {
        if (!artist?.id) return;

        // Listener for Live Status
        const liveStatusUnsubscribe = onSnapshot(doc(db, "live_status", artist.id), (doc) => {
            if (doc.exists()) {
                const statusData = doc.data();
                setLiveStatus(statusData);
                setSessionId(statusData.session_id || null);
            } else {
                setLiveStatus({ is_live: false });
                setSessionId(null);
            }
        });

        // Listener for Requests (only if a session is active)
        let requestsUnsubscribe = () => {};
        if (sessionId) {
            const requestsQuery = query(
                collection(db, 'requests'),
                where('artist_id', '==', artist.id),
                where('session_id', '==', sessionId)
            );
            requestsUnsubscribe = onSnapshot(requestsQuery, (snapshot) => {
                const requestsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setRequests(requestsData);
            });
        } else {
            setRequests([]); // Clear requests if no active session
        }

        // Listener for Latest Tips
        const tipsQuery = query(
            collection(db, 'tips'),
            where('artist_id', '==', artist.id)
        );
        const tipsUnsubscribe = onSnapshot(tipsQuery, (snapshot) => {
            const now = new Date();
            const fiveMinutesAgo = new Date(now.getTime() - (5 * 60 * 1000));
            
            const tipsData = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(tip => {
                    const tipTime = tip.created_at?.toDate();
                    return tipTime && tipTime > fiveMinutesAgo;
                })
                .sort((a, b) => b.created_at?.toDate() - a.created_at?.toDate())
                .slice(0, 2); // Only show maximum 2 tips
            
            if (tipsData.length > 0) {
                setLatestTip(tipsData[0]);
                setRecentTips(tipsData);
            } else {
                setLatestTip(null);
                setRecentTips([]);
            }
        });

        return () => {
            liveStatusUnsubscribe();
            requestsUnsubscribe();
            tipsUnsubscribe();
        };
    }, [artist?.id, sessionId]);

    /** Tie a page_visit row to the current live session so Admin funnel counts match (no duplicate daily_stats). */
    useEffect(() => {
        if (!artist?.id || !artist?.url_slug || !liveStatus?.is_live || !sessionId) return;
        const key = `ysr_lv_pv_${artist.id}_${sessionId}`;
        try {
            if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(key)) return;
            if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, '1');
        } catch (_) {
            /* ignore */
        }
        recordLiveSessionPageVisit(artist.id, artist.url_slug, sessionId);
    }, [artist?.id, artist?.url_slug, liveStatus?.is_live, sessionId]);

    // ✅ Track active viewer - update last seen every 30 seconds
    useEffect(() => {
        if (!artist?.id) return;

        // Update last seen immediately (with small delay to ensure trackPageView completed)
        const initialTimeout = setTimeout(() => {
            updateLastSeen(artist.id);
        }, 1000); // 1 second delay

        // Update every 30 seconds while on page
        const interval = setInterval(() => {
            updateLastSeen(artist.id);
        }, 30000); // 30 seconds

        // Cleanup on unmount
        return () => {
            clearTimeout(initialTimeout);
            clearInterval(interval);
            removeActiveViewer(artist.id);
        };
    }, [artist?.id]);

    // ✅ NEW: Update activePlaylist whenever playlists or liveStatus changes
    useEffect(() => {
        if (!liveStatus || !playlists.length) {
            setActivePlaylist(null);
            return;
        }
        
        const activePl = playlists.find(p => p.id === liveStatus.active_playlist_id);
        setActivePlaylist(activePl || null);
    }, [liveStatus, playlists]);
    
    // Timer logic remains the same
    useEffect(() => {
        if (!liveStatus?.session_end_time || !liveStatus?.is_live) {
            setTimeLeft('');
            return;
        }
        const interval = setInterval(() => {
            // Firestore timestamps need to be converted to JS Dates
            const endTime = liveStatus.session_end_time.toDate();
            const diff = endTime - new Date();
            if (diff <= 0) {
                setTimeLeft('Offline');
                clearInterval(interval);
                return;
            }
            const h = Math.floor(diff / 3600000).toString().padStart(2, '0');
            const m = Math.floor((diff % 3600000) / 60000).toString().padStart(2, '0');
            const s = Math.floor((diff % 60000) / 1000).toString().padStart(2, '0');
            setTimeLeft(`${h}:${m}:${s}`);
        }, 1000);
        return () => clearInterval(interval);
    }, [liveStatus]);

    const playedSongIds = useMemo(() => requests.filter(r => r.status === 'played').map(r => r.song_id), [requests]);

    const popularPickIds = useMemo(() => {
        if (!artist?.show_popular_songs_public || !liveStatus?.is_live) return [];
        const countN = Math.min(6, Math.max(2, Number(artist.popular_songs_display_count) || 3));
        let pool = songs.filter((s) => !s.archived && !playedSongIds.includes(s.id));
        if (activePlaylist) {
            pool = pool.filter((song) => activePlaylist.songs.includes(song.id));
        }
        if (pool.length === 0) return [];
        const featuredRaw = Array.isArray(artist.popular_songs_featured_ids) ? artist.popular_songs_featured_ids : [];
        const poolIds = new Set(pool.map((s) => s.id));
        const featured = featuredRaw.filter((id) => poolIds.has(id));
        const featuredSet = new Set(featured);
        const rest = pool
            .filter((s) => !featuredSet.has(s.id))
            .sort((a, b) => {
                const d = (b.lifetime_request_count || 0) - (a.lifetime_request_count || 0);
                if (d !== 0) return d;
                return (a.order || 0) - (b.order || 0);
            });
        const ids = [...featured];
        for (const s of rest) {
            if (ids.length >= countN) break;
            ids.push(s.id);
        }
        return ids.slice(0, countN);
    }, [artist, songs, playedSongIds, activePlaylist, liveStatus?.is_live]);

    const popularPickSet = useMemo(() => new Set(popularPickIds), [popularPickIds]);

    const popularPicksSongs = useMemo(() => {
        const byId = new Map(songs.map((s) => [s.id, s]));
        return popularPickIds.map((id) => byId.get(id)).filter(Boolean);
    }, [songs, popularPickIds]);

    const songsToDisplay = useMemo(() => {
        let pool = songs.filter((song) => !playedSongIds.includes(song.id));
        if (activePlaylist) {
            pool = pool.filter((song) => activePlaylist.songs.includes(song.id));
        }
        const excludePopular =
            artist?.show_popular_songs_public === true &&
            liveStatus?.is_live === true &&
            popularPickSet.size > 0;
        if (excludePopular) {
            pool = pool.filter((s) => !popularPickSet.has(s.id));
        }

        const songsWithRequestCounts = pool.map((song) => {
            const songRequests = requests.filter((req) => req.song_id === song.id);
            return { ...song, requestCount: songRequests.length };
        });

        const useManualCatalogueOrder = artist?.has_manual_song_order === true;
        if (useManualCatalogueOrder) {
            songsWithRequestCounts.sort(
                (a, b) =>
                    (a.order || 0) - (b.order || 0) ||
                    (a.title || '').localeCompare(b.title || '')
            );
        } else {
            songsWithRequestCounts.sort((a, b) => {
                const lifetimeDiff = (Number(b.lifetime_request_count) || 0) - (Number(a.lifetime_request_count) || 0);
                if (lifetimeDiff !== 0) return lifetimeDiff;
                if (a.requestCount !== b.requestCount) {
                    return b.requestCount - a.requestCount;
                }
                return (a.title || '').localeCompare(b.title || '');
            });
        }

        return songsWithRequestCounts;
    }, [
        songs,
        playedSongIds,
        activePlaylist,
        requests,
        artist?.has_manual_song_order,
        liveStatus?.is_live,
        popularPickSet,
    ]);

    const songsByOriginalArtist = useMemo(() => {
        const map = new Map();
        for (const s of songsToDisplay) {
            const key = String(s.original_artist || 'Unknown').trim() || 'Unknown';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(s);
        }
        return [...map.entries()]
            .sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))
            .map(([originalArtist, groupSongs]) => ({ originalArtist, songs: groupSongs }));
    }, [songsToDisplay]);

    /** All songs findable via search (popular strip + main list, no dupes) */
    const searchableLiveSongs = useMemo(() => {
        const byId = new Map();
        popularPicksSongs.forEach((s) => byId.set(s.id, s));
        songsToDisplay.forEach((s) => byId.set(s.id, s));
        return Array.from(byId.values());
    }, [popularPicksSongs, songsToDisplay]);

    const catalogueSearchFilteredTab = useMemo(() => {
        const q = catalogueSearchQuery.trim().toLowerCase();
        if (!q) return [];
        return searchableLiveSongs.filter((song) => {
            const t = (song.title || '').toLowerCase();
            const a = (song.original_artist || '').toLowerCase();
            return t.includes(q) || a.includes(q);
        });
    }, [searchableLiveSongs, catalogueSearchQuery]);

    const showPopularStrip =
        liveStatus?.is_live === true &&
        artist?.show_popular_songs_public === true &&
        popularPicksSongs.length > 0;
    const showCatalogueTabs = liveStatus?.is_live === true && songsToDisplay.length > 0;

    /** Align element top just below the catalogue tab bar (fixed or in-flow) so sections aren’t clipped. */
    const pinElementBelowStickyChrome = useCallback(
        (el, gapPx = 12) => {
            if (typeof window === 'undefined' || !el) return;
            const bar = catalogueTabsBarRef.current;
            const targetTop =
                showCatalogueTabs && bar ? bar.getBoundingClientRect().bottom + gapPx : gapPx;
            const delta = el.getBoundingClientRect().top - targetTop;
            if (Math.abs(delta) > 1) {
                window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
            }
        },
        [showCatalogueTabs]
    );

    const scrollToRequestMusic = useCallback(() => {
        const preferPopular =
            artist?.show_popular_songs_public &&
            liveStatus?.is_live &&
            popularPickIds.length > 0;
        const el = preferPopular ? popularSectionRef.current : requestSectionRef.current;
        if (el) pinElementBelowStickyChrome(el, 12);
        setPulseTarget(preferPopular ? 'popular' : 'songlist');
        window.setTimeout(() => setPulseTarget(null), 1400);
    }, [
        artist?.show_popular_songs_public,
        liveStatus?.is_live,
        popularPickIds.length,
        pinElementBelowStickyChrome,
    ]);

    const openTipFromSearch = useCallback(() => {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('ysrCloseSongRequestModals'));
        }
        const pinTip = () => {
            if (tipJarSectionRef.current) {
                pinElementBelowStickyChrome(tipJarSectionRef.current, 12);
            }
        };
        pinTip();
        requestAnimationFrame(() => {
            pinTip();
            requestAnimationFrame(() => setTipModalOpen(true));
        });
    }, [pinElementBelowStickyChrome]);

    const jumpToPopularFromSearch = useCallback(() => {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('ysrCloseSongRequestModals'));
        }
        setCatalogueSearchQuery('');
        suppressNextSongsScrollRestoreRef.current = true;
        setCatalogueViewTab('songs');
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const pop = popularSectionRef.current;
                if (pop) {
                    pinElementBelowStickyChrome(pop, 12);
                    setPulseTarget('popular');
                    window.setTimeout(() => setPulseTarget(null), 1400);
                } else {
                    scrollToRequestMusic();
                }
            });
        });
    }, [pinElementBelowStickyChrome, scrollToRequestMusic]);

    useEffect(() => {
        if (liveStatus?.is_live !== true) {
            setCatalogueSearchQuery('');
            setCatalogueSearchFocused(false);
            setCatalogueViewTab('songs');
        }
    }, [liveStatus?.is_live]);

    useEffect(() => {
        if (catalogueViewTab !== 'search') {
            setCatalogueSearchQuery('');
            setCatalogueSearchFocused(false);
        }
    }, [catalogueViewTab]);

    /** Instantly move viewport so the Artists list starts just below the tab bar (no animated scroll). */
    const scrollArtistsListToTopUnderTabs = useCallback(() => {
        if (typeof window === 'undefined') return;
        const list = catalogueArtistsListRef.current;
        const bar = catalogueTabsBarRef.current;
        if (!list || !bar) return;
        const gapPx = 10;
        const targetTop = bar.getBoundingClientRect().bottom + gapPx;
        const listTop = list.getBoundingClientRect().top;
        const delta = listTop - targetTop;
        if (Math.abs(delta) > 2) {
            window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
        }
    }, []);

    useLayoutEffect(() => {
        const prev = prevCatalogueViewTabRef.current;
        const cur = catalogueViewTab;
        if (!showCatalogueTabs) {
            prevCatalogueViewTabRef.current = cur;
            return;
        }
        if (prev !== cur) {
            if (prev === 'songs') {
                songsCatalogueScrollYRef.current = window.scrollY;
            }
            if (cur === 'artists') {
                scrollArtistsListToTopUnderTabs();
            }
            if (cur === 'songs' && songsCatalogueScrollYRef.current != null) {
                if (suppressNextSongsScrollRestoreRef.current) {
                    suppressNextSongsScrollRestoreRef.current = false;
                } else {
                    window.scrollTo({
                        top: songsCatalogueScrollYRef.current,
                        left: 0,
                        behavior: 'auto',
                    });
                }
            }
        }
        prevCatalogueViewTabRef.current = cur;
    }, [catalogueViewTab, showCatalogueTabs, scrollArtistsListToTopUnderTabs]);

    /** Keep full “Request a Song” header visible under the tab bar when Search is open. */
    const pinRequestSongHeadingForSearchTab = useCallback(() => {
        if (catalogueViewTab !== 'search' || !showCatalogueTabs) return;
        const el = requestSongHeadingRef.current;
        /** Slightly more gap than body sections so title + subtitle clear the tab bar cleanly. */
        if (el) pinElementBelowStickyChrome(el, 14);
    }, [catalogueViewTab, showCatalogueTabs, pinElementBelowStickyChrome]);

    useLayoutEffect(() => {
        pinRequestSongHeadingForSearchTab();
    }, [
        pinRequestSongHeadingForSearchTab,
        catalogueTabsDocked,
        catalogueTabsBarHeight,
    ]);

    const updateCatalogueTabsDocked = useCallback(() => {
        if (typeof window === 'undefined') return;
        const sentinel = catalogueTabsSentinelRef.current;
        if (!sentinel) return;
        setCatalogueTabsDocked(sentinel.getBoundingClientRect().top <= 0);
    }, []);

    useEffect(() => {
        if (catalogueViewTab === 'search' && catalogueSearchInputRef.current) {
            const pinHeading = () => {
                if (requestSongHeadingRef.current) {
                    pinElementBelowStickyChrome(requestSongHeadingRef.current, 14);
                }
            };
            const t1 = window.setTimeout(() => {
                catalogueSearchInputRef.current?.focus();
                requestAnimationFrame(pinHeading);
            }, 50);
            const t2 = window.setTimeout(pinHeading, 240);
            return () => {
                window.clearTimeout(t1);
                window.clearTimeout(t2);
            };
        }
        return undefined;
    }, [catalogueViewTab, pinElementBelowStickyChrome]);

    useLayoutEffect(() => {
        if (!showCatalogueTabs) {
            setCatalogueTabsDocked(false);
            setCatalogueTabsBarHeight(0);
            return undefined;
        }
        const bar = catalogueTabsBarRef.current;
        const measure = () => {
            const el = catalogueTabsBarRef.current;
            if (el) setCatalogueTabsBarHeight(el.offsetHeight);
        };
        measure();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (bar && ro) ro.observe(bar);
        updateCatalogueTabsDocked();
        return () => {
            if (ro) ro.disconnect();
        };
    }, [showCatalogueTabs, catalogueViewTab, updateCatalogueTabsDocked]);

    useEffect(() => {
        if (!showCatalogueTabs) return undefined;
        updateCatalogueTabsDocked();
        window.addEventListener('scroll', updateCatalogueTabsDocked, { passive: true });
        window.addEventListener('resize', updateCatalogueTabsDocked);
        const vv = typeof window !== 'undefined' ? window.visualViewport : null;
        if (vv) {
            vv.addEventListener('scroll', updateCatalogueTabsDocked);
            vv.addEventListener('resize', updateCatalogueTabsDocked);
        }
        return () => {
            window.removeEventListener('scroll', updateCatalogueTabsDocked);
            window.removeEventListener('resize', updateCatalogueTabsDocked);
            if (vv) {
                vv.removeEventListener('scroll', updateCatalogueTabsDocked);
                vv.removeEventListener('resize', updateCatalogueTabsDocked);
            }
        };
    }, [showCatalogueTabs, updateCatalogueTabsDocked]);

    const showCatalogueSearchHint =
        !catalogueSearchQuery && !catalogueSearchFocused;

    const catalogueTabStrip = showCatalogueTabs ? (
        <>
            <div
                ref={catalogueTabsSentinelRef}
                className="pointer-events-none h-px w-full shrink-0"
                aria-hidden
            />
            <div
                className={catalogueTabsDocked ? 'mb-4' : ''}
                style={
                    catalogueTabsDocked && catalogueTabsBarHeight > 0
                        ? { minHeight: catalogueTabsBarHeight }
                        : undefined
                }
            >
                <div
                    ref={catalogueTabsBarRef}
                    role="presentation"
                    className={
                        catalogueTabsDocked
                            ? 'fixed left-0 right-0 z-50 isolate border-b border-white/10 bg-[#050816] py-2 pl-4 pr-4 shadow-[0_12px_32px_rgba(0,0,0,0.55)]'
                            : 'relative z-40 -mx-1 mb-4 border-b border-white/10 bg-[#050816]/92 py-2 backdrop-blur-md supports-[backdrop-filter]:bg-[#050816]/78 shadow-[0_10px_28px_rgba(2,6,23,0.45)]'
                    }
                    style={
                        catalogueTabsDocked
                            ? { top: 'env(safe-area-inset-top, 0px)' }
                            : undefined
                    }
                >
        <div
            className="flex flex-wrap items-center gap-1 rounded-2xl border border-white/10 bg-black/25 p-1 shadow-inner shadow-black/20"
            role="tablist"
            aria-label="Browse catalogue"
        >
            <button
                type="button"
                role="tab"
                aria-selected={catalogueViewTab === 'songs'}
                onClick={() => setCatalogueViewTab('songs')}
                className={`min-h-[40px] flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition-colors sm:flex-none ${
                    catalogueViewTab === 'songs'
                        ? 'bg-blue-500/35 text-white shadow-md'
                        : 'text-blue-100/70 hover:bg-white/5'
                }`}
            >
                <span className="inline-flex items-center justify-center gap-1.5">
                    <Music className="h-4 w-4 opacity-90" aria-hidden />
                    Songs
                </span>
            </button>
            <button
                type="button"
                role="tab"
                aria-selected={catalogueViewTab === 'artists'}
                onClick={() => setCatalogueViewTab('artists')}
                className={`min-h-[40px] flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition-colors sm:flex-none ${
                    catalogueViewTab === 'artists'
                        ? 'bg-blue-500/35 text-white shadow-md'
                        : 'text-blue-100/70 hover:bg-white/5'
                }`}
            >
                <span className="inline-flex items-center justify-center gap-1.5">
                    <Users className="h-4 w-4 opacity-90" aria-hidden />
                    Artists
                </span>
            </button>
            <button
                type="button"
                role="tab"
                aria-selected={catalogueViewTab === 'search'}
                onClick={() => setCatalogueViewTab('search')}
                title="Search"
                aria-label="Search songs"
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors ${
                    catalogueViewTab === 'search'
                        ? 'bg-blue-500/35 text-white shadow-md'
                        : 'text-blue-100/70 hover:bg-white/5'
                }`}
            >
                <Search className="h-4 w-4" strokeWidth={2.25} />
            </button>
        </div>
                </div>
            </div>
        </>
    ) : null;

    // Handle tip modal form changes
    const handleTipFormChange = (e) => {
        const { name, value } = e.target;
        setTipFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle tip submission - now requires payment first
    const handleTipSubmit = async (e) => {
        e.preventDefault();
        if (!tipFormData.name || !tipFormData.amount) return;
        
        const tipAmount = parseFloat(tipFormData.amount);
        if (tipAmount <= 0) {
            toast({
                title: 'Invalid Amount',
                description: 'Please enter a valid tip amount.',
                variant: 'destructive'
            });
            return;
        }
        
        // Show payment modal instead of submitting directly
        setShowTipPaymentModal(true);
    };
    
    // Handle tip payment success
    const handleTipPaymentSuccess = async (paymentResult) => {
        setTipPaymentData(paymentResult);
        setShowTipPaymentModal(false);

        // Trust Payments backend already creates tip-only requests.
        setTipFormData({ name: '', amount: '', note: '' });
        setTipModalOpen(false);
        setTipPaymentData(null);

        const viaPayPal = isPayPalPaymentProvider(paymentResult?.paymentProvider);
        toast({
            title: viaPayPal ? 'Tip sent' : 'Tip Sent!',
            description: viaPayPal
                ? `Thanks! Your ${formatArtistCurrency(parseFloat(tipFormData.amount || paymentResult?.amount || 0))} tip was processed securely through PayPal.`
                : `Thanks for supporting ${artist?.artist_name || 'the artist'}!`,
            variant: 'default'
        });
    };
    
    const handleTipPaymentCancel = () => {
        setShowTipPaymentModal(false);
        setTipPaymentData(null);
    };

    // Prevent modal background scroll
    useEffect(() => {
        if (tipModalOpen) {
            document.body.style.overflow = 'hidden';
            window.scrollTo(0, 0);
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [tipModalOpen]);

    // Generate initials from artist name
    const getInitials = (name) => {
        if (!name) return '';
        const words = name.trim().split(' ');
        if (words.length === 1) {
            return words[0].charAt(0).toUpperCase();
        } else {
            return words.slice(0, 2).map(word => word.charAt(0).toUpperCase()).join('');
        }
    };

    if (loading) return <div className={`${ysr2Mobile.shell} flex min-h-screen items-center justify-center`}>Loading...</div>;
    if (error) return <div className={`${ysr2Mobile.shell} flex min-h-screen items-center justify-center p-6 text-center`}>{error}</div>;
    if (!artist) return <div className={`${ysr2Mobile.shell} flex min-h-screen items-center justify-center`}>Artist not found.</div>;

    /** Same as `ysr2Mobile.surface` but no backdrop-filter — blur on an ancestor breaks `position: sticky` for the catalogue tabs. */
    const requestSongCardSurface = String(ysr2Mobile.surface).replace(/\s*backdrop-blur-xl\s*/g, ' ').trim();

    return (
        <div
            className={`${String(ysr2Mobile.shell).replace(/\boverflow-hidden\b/, 'overflow-x-clip')} text-white ${liveStatus?.is_live && basketSongs.length > 0 ? 'pb-32' : ''}`}
        >
            <div className={ysr2Mobile.orbBlue} />
            <div className={ysr2Mobile.orbPurple} />
            <Helmet>
                <title>{artist.artist_name} - Your Song Request</title>
                <meta name="description" content={`Request songs from ${artist.artist_name} or send a tip.`} />
            </Helmet>

            {user?.uid === artist.id && (
                <div className="fixed top-3 right-3 z-[200] flex items-center">
                    <UserNotificationCenter />
                </div>
            )}

            {/* Header Image with Profile Picture Overlapping */}
            <div className="relative z-10">
                {artist.header_image_url && (
                    <div className="w-full h-64 md:h-80 lg:h-96 relative overflow-hidden rounded-b-[2rem] border-b border-blue-300/15">
                        <img 
                            src={artist.header_image_url} 
                            alt={`${artist.artist_name} header`}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-[#050816]" />
                    </div>
                )}
                
                {/* Profile Picture - Positioned conditionally based on header */}
                <div className={`relative flex justify-center mb-4 ${artist.header_image_url ? '-mt-16 md:-mt-20 lg:-mt-24' : 'mt-8'}`}>
                    <div className="relative">
                        <div className={`${liveStatus?.is_live ? 'w-24 h-24 md:w-28 md:h-28 lg:w-32 lg:h-32' : 'w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48'} rounded-full overflow-hidden border-4 ${liveStatus?.is_live ? 'border-emerald-300/70 shadow-[0_0_45px_rgba(16,185,129,0.32)]' : 'border-blue-300/35 shadow-[0_0_55px_rgba(37,99,235,0.22)]'} bg-slate-950 flex items-center justify-center`}>
                            {artist.profile_picture_url ? (
                                <img src={artist.profile_picture_url} alt={`${artist.artist_name} profile`} className="w-full h-full object-cover" />
                            ) : (
                                <span className={`text-blue-100 font-black ${liveStatus?.is_live ? 'text-2xl md:text-3xl lg:text-4xl' : 'text-4xl md:text-5xl lg:text-6xl'}`}>{getInitials(artist.artist_name)}</span>
                            )}
                        </div>
                        {liveStatus?.is_live && (
                            <div className="absolute -bottom-1 -right-1 bg-red-600 text-white text-xs font-black px-3 py-1 rounded-full animate-pulse z-10 shadow-[0_0_28px_rgba(239,68,68,0.55)]">
                                LIVE
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Paused Requests Banner */}
            {liveStatus?.is_live && liveStatus?.accepting_new_requests === false && (
                <div className="relative z-10 px-4 mb-4">
                    <div className={`${ysr2Mobile.amberCard} p-3 flex items-center space-x-3`}>
                        <Clock className="w-5 h-5 text-orange-400 flex-shrink-0" />
                        <div className="flex-1">
                            <p className="text-orange-100 font-medium text-sm">
                                {artist.artist_name} has paused new requests
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {(() => {
                try {
                    return (
                        <>
                            {/* Mobile-First Hero Section */}
                            <div className="relative z-10 px-4 pb-8">
                                {/* Artist Name and Info */}
                                <div className={`${ysr2Mobile.surfaceStrong} flex flex-col items-center text-center mb-6 p-6`}>
                                    <span className={ysr2Mobile.pill}>{liveStatus?.is_live ? 'Live performance' : 'Artist profile'}</span>
                                    <h1 className={`${liveStatus?.is_live ? 'text-3xl' : 'text-4xl'} font-black tracking-tight mt-3 mb-2`}>{artist.artist_name}</h1>
                                    {artist.tagline && !liveStatus?.is_live && (
                                        <p className="text-gray-300 text-lg mb-3 italic max-w-md">{artist.tagline}</p>
                                    )}
                                    {liveStatus?.is_live ? (
                                        <div className="bg-red-500/10 border border-red-300/25 rounded-full px-3 py-1 shadow-[0_0_24px_rgba(239,68,68,0.18)]">
                                            <div className="flex items-center justify-center space-x-2 text-red-300">
                                                <Clock className="w-3 h-3" />
                                                <span className="font-mono text-sm">{timeLeft || '00:00:00'}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-white/[0.06] border border-white/10 rounded-full px-4 py-2 mb-4">
                                            <span className="text-slate-300">● Currently Offline</span>
                                        </div>
                                    )}
                                    
                                    {/* Social Media Links - Show when offline */}
                                    {!liveStatus?.is_live && (artist.social_facebook || artist.social_x || artist.social_instagram || artist.social_tiktok) && (
                                        <div className="flex items-center justify-center space-x-4 mt-4">
                                            {artist.social_facebook && (
                                                <a 
                                                    href={`https://facebook.com/${artist.social_facebook}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-gray-400 hover:text-blue-500 transition-colors"
                                                    aria-label="Facebook"
                                                >
                                                    <Facebook className="w-5 h-5" />
                                                </a>
                                            )}
                                            {artist.social_x && (
                                                <a 
                                                    href={`https://x.com/${artist.social_x}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-gray-400 hover:text-gray-200 transition-colors"
                                                    aria-label="X (Twitter)"
                                                >
                                                    <Twitter className="w-5 h-5" />
                                                </a>
                                            )}
                                            {artist.social_instagram && (
                                                <a 
                                                    href={`https://instagram.com/${artist.social_instagram}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-gray-400 hover:text-pink-500 transition-colors"
                                                    aria-label="Instagram"
                                                >
                                                    <Instagram className="w-5 h-5" />
                                                </a>
                                            )}
                                            {artist.social_tiktok && (
                                                <a 
                                                    href={`https://tiktok.com/@${artist.social_tiktok}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-gray-400 hover:text-cyan-400 transition-colors"
                                                    aria-label="TikTok"
                                                >
                                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a4.83 4.83 0 0 0-1-.1 4.85 4.85 0 0 0-4.83 4.83 4.83 4.83 0 0 0 8.47 3.27l.01-.01v-7.19a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.32-.27z"/>
                                                    </svg>
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Live Request Queue (when live and has requests) */}
                                {liveStatus?.is_live && (
                                    <div className={`${ysr2Mobile.glowCard} p-4 mb-6`}>
                                        <div className="flex items-center space-x-3 mb-4">
                                            <div className="w-10 h-10 bg-purple-500/20 border border-purple-300/25 rounded-2xl flex items-center justify-center">
                                                <Music className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-white">Live Request Queue</h2>
                                                <p className="text-purple-100/75 text-sm">
                                                    Songs requested by the audience during this live set
                                                </p>
                                            </div>
                                        </div>
                                        
                                        {(() => {
                                            const activeQueueRequests = requests.filter((req) => req.status !== 'played');
                                            return activeQueueRequests.length === 0 ? (
                                            <div className="text-center py-3 px-2">
                                                <p className="text-purple-200 text-sm leading-snug">
                                                    No requests in the queue yet.{' '}
                                                    <button
                                                        type="button"
                                                        onClick={scrollToRequestMusic}
                                                        className="text-purple-100 font-medium underline decoration-purple-400/80 underline-offset-2 hover:text-white active:scale-[0.98] transition-transform"
                                                    >
                                                        Be the first
                                                    </button>
                                                </p>
                                            </div>
                                                            ) : (
                                                <div className="space-y-2">
                                                    {(() => {
                                                        const groupedRequests = activeQueueRequests.reduce((acc, request) => {
                                                            const key = request.song_id || request.song_title;
                                                            if (!acc[key]) {
                                                                acc[key] = {
                                                                    song_title: request.song_title,
                                                                    original_artist: request.original_artist,
                                                                    count: 0,
                                                                    requests: []
                                                                };
                                                            }
                                                            acc[key].count++;
                                                            acc[key].requests.push(request);
                                                            return acc;
                                                        }, {});

                                                        const sortedGroups = Object.values(groupedRequests)
                                                            .sort((a, b) => b.count - a.count)
                                                            .slice(0, 3);

                                                        return sortedGroups.map((group) => (
                                                            <div key={group.song_title} className="bg-white/[0.055] border border-white/10 rounded-2xl p-3">
                                                                <div className="flex items-center justify-between">
                                                                    <div>
                                                                        <p className="text-white font-medium">{group.song_title}</p>
                                                                        <p className="text-purple-200 text-sm">by {group.original_artist || 'Unknown'}</p>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <div className="flex items-center space-x-1 text-xs text-purple-300">
                                                                            <User className="w-3 h-3" />
                                                                            <span>{group.count}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ));
                                                    })()}
                                                {activeQueueRequests.length > 3 && (
                                                    <p className="text-purple-300 text-sm text-center pt-2">
                                                        +{activeQueueRequests.length - 3} more in queue
                                                    </p>
                                                )}
                                            </div>
                                        );
                                        })()}
                                    </div>
                                )}

                                {liveStatus?.is_live &&
                                    artist?.show_popular_songs_public === true &&
                                    popularPicksSongs.length > 0 && (
                                        <div
                                            ref={popularSectionRef}
                                        className={`${ysr2Mobile.amberCard} p-4 mb-6 transition-shadow duration-500 ${
                                                pulseTarget === 'popular'
                                                    ? 'ring-2 ring-amber-400/90 shadow-lg shadow-amber-900/40'
                                                    : ''
                                            }`}
                                        >
                                            <div className="flex items-center space-x-3 mb-4">
                                                <div className="w-10 h-10 bg-amber-500/20 border border-amber-300/25 rounded-2xl flex items-center justify-center">
                                                    <TrendingUp className="w-5 h-5 text-white" />
                                                </div>
                                                <div>
                                                    <h2 className="text-xl font-bold text-white">Popular picks</h2>
                                                    <p className="text-amber-100/90 text-sm">
                                                        Crowd favourites — jump in and request one you know
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="space-y-3">
                                                {popularPicksSongs.map((song) => (
                                                    <SongCard
                                                        key={`popular-${song.id}`}
                                                        song={song}
                                                        artistId={artist.id}
                                                        sessionId={sessionId}
                                                        isLive={liveStatus?.is_live || false}
                                                        isPlayed={playedSongIds.includes(song.id)}
                                                        acceptingNewRequests={liveStatus?.accepting_new_requests !== false}
                                                        mode="artist"
                                                        theme="artist"
                                                        artistCurrency={artistCurrency}
                                                        artistCountry={artist?.country}
                                                        basketMode={liveStatus?.is_live === true}
                                                        inBasket={basketIds.has(song.id)}
                                                        onToggleBasket={handleToggleBasket}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                {showCatalogueTabs && showPopularStrip ? (
                                    <div className="mb-3">{catalogueTabStrip}</div>
                                ) : null}

                                {/* Request a Song Section - Only show when live */}
                                {liveStatus?.is_live && (
                                    <div
                                        ref={requestSectionRef}
                                        className={`${requestSongCardSurface} bg-white/[0.065] p-4 mb-6 transition-shadow duration-500 ${
                                            pulseTarget === 'songlist'
                                                ? 'ring-2 ring-blue-400/90 shadow-lg shadow-blue-900/40'
                                                : ''
                                        }`}
                                    >
                                        <div
                                            ref={requestSongHeadingRef}
                                            className="flex items-center space-x-3 mb-4"
                                        >
                                            <div className="w-10 h-10 bg-blue-500/20 border border-blue-300/25 rounded-2xl flex items-center justify-center">
                                                <Music className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-white">Request a Song</h2>
                                                <p className="text-blue-100/75 text-sm">
                                                    Choose a song to request during this live performance
                                                </p>
                                                <p className="text-blue-100/55 text-xs mt-0.5">
                                                    Add more than one — they check out together in a single payment
                                                </p>
                                            </div>
                                        </div>

                                        {showCatalogueTabs && !showPopularStrip ? catalogueTabStrip : null}
                                        
                                        {songsToDisplay.length === 0 ? (
                                            <div className="bg-black/30 border border-white/10 rounded-2xl p-6 text-center">
                                                <p className="text-gray-400">No songs available at the moment.</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {catalogueViewTab === 'songs' &&
                                                    songsToDisplay.map((song) => (
                                                        <SongCard
                                                            key={song.id}
                                                            song={song}
                                                            artistId={artist.id}
                                                            sessionId={sessionId}
                                                            isLive={liveStatus?.is_live || false}
                                                            isPlayed={playedSongIds.includes(song.id)}
                                                            acceptingNewRequests={
                                                                liveStatus?.accepting_new_requests !== false
                                                            }
                                                            mode="artist"
                                                            theme="artist"
                                                            artistCurrency={artistCurrency}
                                                            artistCountry={artist?.country}
                                                            basketMode={liveStatus?.is_live === true}
                                                            inBasket={basketIds.has(song.id)}
                                                            onToggleBasket={handleToggleBasket}
                                                        />
                                                    ))}
                                                {catalogueViewTab === 'artists' && (
                                                    <div ref={catalogueArtistsListRef} className="space-y-3">
                                                        {songsByOriginalArtist.map((group) => (
                                                            <div key={group.originalArtist} className="space-y-2">
                                                                <h3 className="border-b border-white/10 px-1 pb-1 text-base font-bold tracking-tight text-blue-100/90">
                                                                    {group.originalArtist}
                                                                </h3>
                                                                <div className="space-y-2">
                                                                    {group.songs.map((song) => (
                                                                        <SongCard
                                                                            key={`by-artist-${song.id}`}
                                                                            song={song}
                                                                            artistId={artist.id}
                                                                            sessionId={sessionId}
                                                                            isLive={liveStatus?.is_live || false}
                                                                            isPlayed={playedSongIds.includes(song.id)}
                                                                            acceptingNewRequests={
                                                                                liveStatus?.accepting_new_requests !==
                                                                                false
                                                                            }
                                                                            mode="artist"
                                                                            theme="artist"
                                                                            artistCurrency={artistCurrency}
                                                                            artistCountry={artist?.country}
                                                                            subtitlePriceInline
                                                                            basketMode={liveStatus?.is_live === true}
                                                                            inBasket={basketIds.has(song.id)}
                                                                            onToggleBasket={handleToggleBasket}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {catalogueViewTab === 'search' && (
                                                    <>
                                                        <div
                                                            ref={catalogueSearchPanelRef}
                                                            className="relative z-10 isolate rounded-2xl border border-violet-400/40 bg-[#1a1228] p-3 shadow-lg ring-1 ring-white/10"
                                                        >
                                                            <label
                                                                htmlFor="live-catalogue-search-tab"
                                                                className="sr-only"
                                                            >
                                                                Search songs
                                                            </label>
                                                            <div className="flex items-center gap-2">
                                                                <div
                                                                    className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-violet-400/40 bg-violet-950/90"
                                                                    aria-hidden
                                                                >
                                                                    <Search
                                                                        className="h-5 w-5 text-violet-100"
                                                                        strokeWidth={2}
                                                                    />
                                                                </div>
                                                                <div className="relative min-w-0 flex-1">
                                                                    <Input
                                                                        ref={catalogueSearchInputRef}
                                                                        id="live-catalogue-search-tab"
                                                                        type="text"
                                                                        inputMode="search"
                                                                        enterKeyHint="search"
                                                                        autoComplete="off"
                                                                        value={catalogueSearchQuery}
                                                                        onChange={(e) =>
                                                                            setCatalogueSearchQuery(e.target.value)
                                                                        }
                                                                        onFocus={() => setCatalogueSearchFocused(true)}
                                                                        onBlur={() =>
                                                                            setCatalogueSearchFocused(false)
                                                                        }
                                                                        placeholder=""
                                                                        aria-label="Search songs by title or artist"
                                                                        className="relative z-10 h-10 w-full rounded-xl border border-violet-400/30 bg-zinc-950/95 px-3 text-base text-white shadow-inner placeholder:text-transparent focus-visible:ring-violet-400/45"
                                                                    />
                                                                    {showCatalogueSearchHint && (
                                                                        <span
                                                                            className="pointer-events-none absolute inset-y-0 left-0 right-0 z-0 flex items-center truncate rounded-xl px-3 text-sm text-violet-200/55"
                                                                            aria-hidden
                                                                        >
                                                                            Search song or artist…
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {!catalogueSearchQuery.trim() ? (
                                                            <p className="py-4 text-center text-sm text-gray-400">
                                                                Type a song or artist name — matches anywhere in
                                                                the title or original artist.
                                                            </p>
                                                        ) : catalogueSearchFilteredTab.length > 0 ? (
                                                            catalogueSearchFilteredTab.map((song) => (
                                                                <SongCard
                                                                    key={`req-find-${song.id}`}
                                                                    song={song}
                                                                    artistId={artist.id}
                                                                    sessionId={sessionId}
                                                                    isLive={liveStatus?.is_live || false}
                                                                    isPlayed={playedSongIds.includes(song.id)}
                                                                    acceptingNewRequests={
                                                                        liveStatus?.accepting_new_requests !==
                                                                        false
                                                                    }
                                                                    mode="artist"
                                                                    theme="artist"
                                                                    artistCurrency={artistCurrency}
                                                                    artistCountry={artist?.country}
                                                                    basketMode={liveStatus?.is_live === true}
                                                                    inBasket={basketIds.has(song.id)}
                                                                    onToggleBasket={handleToggleBasket}
                                                                />
                                                            ))
                                                        ) : (
                                                            <div className="rounded-2xl border border-white/10 bg-black/30 p-5 text-center text-sm leading-relaxed text-gray-300">
                                                                <p className="mb-2">
                                                                    Nothing matches. Speak to the artist
                                                                    directly and{' '}
                                                                    <button
                                                                        type="button"
                                                                        onClick={openTipFromSearch}
                                                                        className="font-semibold text-violet-300 underline decoration-violet-400/80 underline-offset-2 hover:text-violet-200"
                                                                    >
                                                                        send a tip
                                                                    </button>
                                                                    .
                                                                </p>
                                                                <p>
                                                                    Or try a{' '}
                                                                    <button
                                                                        type="button"
                                                                        onClick={jumpToPopularFromSearch}
                                                                        className="border-0 bg-transparent p-0 align-baseline font-semibold underline decoration-amber-400/90 underline-offset-2 hover:opacity-95"
                                                                    >
                                                                        <span className="inline bg-gradient-to-r from-violet-200 via-amber-400 via-40% to-violet-200 bg-[length:240%_100%] bg-clip-text font-semibold text-transparent animate-ysr-popular-sheen [text-shadow:0_0_18px_rgba(251,146,60,0.35)]">
                                                                            popular pick
                                                                        </span>
                                                                    </button>
                                                                    .
                                                                </p>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Recent Tips Section (when available) */}
                                {recentTips.length > 0 && (
                                    <div className={`${ysr2Mobile.amberCard} p-4 mb-6`}>
                                        <div className="flex items-center space-x-3 mb-3">
                                            <div className="w-10 h-10 bg-amber-500/20 border border-amber-300/25 rounded-2xl flex items-center justify-center">
                                                <Heart className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-white">Recent Tips</h2>
                                                <p className="text-amber-200 text-sm">
                                                    Recent support from the audience
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="space-y-2">
                                            {recentTips.map((tip, index) => (
                                                <div key={tip.id} className="bg-white/[0.055] border border-white/10 rounded-2xl p-3">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-3">
                                                            <Heart className="w-5 h-5 text-amber-400" />
                                                            <span className="text-2xl font-bold text-white">
                                                                {formatArtistCurrency(Number(tip.amount))}
                                                            </span>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-amber-200 text-sm">from {tip.name}</p>
                                                            <p className="text-amber-300 text-xs">{getRelativeTime(tip.created_at)}</p>
                                                        </div>
                                                    </div>
                                                    {tip.message && (
                                                        <p className="text-amber-100 text-sm mt-2 italic">"{tip.message}"</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Artist Bio Section - Show when offline or when bio exists */}
                                {artist.bio && (!liveStatus?.is_live || (liveStatus?.is_live && requests.length === 0)) && (
                                    <div className={`${ysr2Mobile.surface} p-4 mb-6`}>
                                        <div className="flex items-center space-x-3 mb-4">
                                            <div className="w-10 h-10 bg-white/10 border border-white/10 rounded-2xl flex items-center justify-center">
                                                <User className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-white">About {artist.artist_name}</h2>
                                                <p className="text-gray-200 text-sm">
                                                    Learn more about the artist
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="bg-black/30 border border-white/10 rounded-2xl p-4">
                                            <p className="text-gray-100 leading-relaxed whitespace-pre-wrap">
                                                {artist.bio}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Upcoming Gigs Section (when available and not live) */}
                                {gigs.length > 0 && !liveStatus?.is_live && (
                                    <div className={`${ysr2Mobile.liveCard} p-6 mb-6`}>
                                        <div className="flex items-center space-x-3 mb-6">
                                            <div className="w-12 h-12 bg-gradient-to-br from-green-500/25 to-emerald-500/20 border border-emerald-300/25 rounded-2xl flex items-center justify-center shadow-lg">
                                                <Calendar className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-2xl font-bold text-white">Upcoming Gigs</h2>
                                                <p className="text-green-200 text-sm">
                                                    Catch {artist.artist_name} live
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="space-y-4">
                                            {gigs.slice(0, 5).map((gig) => (
                                                <div key={gig.id} className="bg-white/[0.055] backdrop-blur-sm rounded-2xl p-5 border border-white/10 hover:border-emerald-300/35 transition-all">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <h3 className="text-white font-bold text-lg mb-2">{gig.venue_name}</h3>
                                                            <div className="flex flex-wrap items-center gap-4 text-sm text-green-200 mb-2">
                                                                <div className="flex items-center space-x-2">
                                                                    <Calendar className="w-4 h-4" />
                                                                    <span className="font-medium">{new Date(gig.date).toLocaleDateString('en-GB', { 
                                                                        weekday: 'long', 
                                                                        day: 'numeric', 
                                                                        month: 'long',
                                                                        year: 'numeric'
                                                                    })}</span>
                                                                </div>
                                                                {gig.time && (
                                                                    <div className="flex items-center space-x-2">
                                                                        <Clock className="w-4 h-4" />
                                                                        <span className="font-medium">{gig.time}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {(gig.address || gig.postcode) && (
                                                                <div className="flex items-center space-x-2 text-sm text-green-300 mt-2">
                                                                    <MapPin className="w-4 h-4" />
                                                                    <span>{gig.address} {gig.postcode}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            {gigs.length > 5 && (
                                                <p className="text-green-300 text-sm text-center pt-2 font-medium">
                                                    +{gigs.length - 5} more gigs scheduled
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Tip Jar Section */}
                                <div ref={tipJarSectionRef} className={`${ysr2Mobile.amberCard} p-4 mb-6`}>
                                    <div className="flex items-center space-x-3 mb-4">
                                        <div className="w-10 h-10 bg-amber-500/20 border border-amber-300/25 rounded-2xl flex items-center justify-center">
                                            <Heart className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold text-white">💛 Tip Jar</h2>
                                            <p className="text-amber-200 text-sm">
                                                Enjoying the show? Show your appreciation!
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <button
                                        type="button"
                                        onClick={() => setTipModalOpen(true)}
                                        className="relative z-10 w-full bg-amber-500 hover:bg-amber-400 text-black font-black py-3 px-6 rounded-2xl transition-colors flex items-center justify-center space-x-2 shadow-[0_16px_45px_rgba(245,158,11,0.25)]"
                                    >
                                        <PoundSterling className="w-5 h-5" />
                                        <span>Send a Tip</span>
                                    </button>
                                </div>
                            </div>

                            {/* Tip Modal — portaled above song-request overlays (SongCard uses z-[100000]) */}
                            {tipModalOpen &&
                                createPortal(
                                    <div
                                        className="fixed inset-0 bg-black/80 backdrop-blur-sm"
                                        style={{ zIndex: 200000 }}
                                        role="presentation"
                                        onClick={() => setTipModalOpen(false)}
                                    >
                                        <div
                                            className="fixed left-1/2 top-4 w-full max-w-sm -translate-x-1/2 rounded-[2rem] border border-blue-300/20 bg-[#050816] p-6 shadow-[0_24px_90px_rgba(37,99,235,0.22)]"
                                            style={{
                                                zIndex: 200001,
                                                maxHeight: 'calc(100vh - 32px)',
                                                overflowY: 'auto',
                                            }}
                                            role="dialog"
                                            aria-modal="true"
                                            aria-labelledby="tip-modal-title"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {/* Close Button */}
                                            <button
                                                type="button"
                                                onClick={() => setTipModalOpen(false)}
                                                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/10 text-gray-300 transition-colors hover:bg-white/15 hover:text-white"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>

                                            {/* Header */}
                                            <div className="mb-6">
                                                <h2
                                                    id="tip-modal-title"
                                                    className="mb-2 text-xl font-bold text-white"
                                                >
                                                    Send a Tip to {artist.artist_name}
                                                </h2>
                                                <p className="text-sm text-gray-300">
                                                    Your generosity is greatly appreciated!
                                                </p>
                                            </div>

                                            {/* Form */}
                                            <form onSubmit={handleTipSubmit} className="space-y-4">
                                                <div>
                                                    <Label
                                                        htmlFor="tip-name"
                                                        className="text-sm font-medium text-gray-200"
                                                    >
                                                        Your Name
                                                    </Label>
                                                    <Input
                                                        id="tip-name"
                                                        name="name"
                                                        value={tipFormData.name}
                                                        onChange={handleTipFormChange}
                                                        required
                                                        className={`mt-1 ${ysr2Mobile.input} w-full text-base`}
                                                        style={{ fontSize: '16px' }}
                                                    />
                                                </div>

                                                <div>
                                                    <Label
                                                        htmlFor="tip-amount"
                                                        className="text-sm font-medium text-gray-200"
                                                    >
                                                        Tip Amount ({getCurrencySymbol(artistCurrency)})
                                                    </Label>
                                                    <Input
                                                        id="tip-amount"
                                                        name="amount"
                                                        type="number"
                                                        step="0.01"
                                                        min="1"
                                                        value={tipFormData.amount}
                                                        onChange={handleTipFormChange}
                                                        placeholder="5.00"
                                                        required
                                                        className={`mt-1 ${ysr2Mobile.input} w-full text-base`}
                                                        style={{ fontSize: '16px' }}
                                                    />
                                                </div>

                                                <div>
                                                    <Label
                                                        htmlFor="tip-note"
                                                        className="text-sm font-medium text-gray-200"
                                                    >
                                                        Note for artist (optional)
                                                    </Label>
                                                    <Textarea
                                                        id="tip-note"
                                                        name="note"
                                                        value={tipFormData.note}
                                                        onChange={handleTipFormChange}
                                                        className={`mt-1 ${ysr2Mobile.input} min-h-[80px] w-full text-base`}
                                                        style={{ fontSize: '16px' }}
                                                        placeholder="Leave a message for the artist..."
                                                    />
                                                </div>

                                                <Button
                                                    type="submit"
                                                    disabled={isSubmittingTip}
                                                    className="w-full rounded-2xl bg-amber-500 px-6 py-3 font-black text-black shadow-[0_16px_45px_rgba(245,158,11,0.25)] hover:bg-amber-400 disabled:opacity-50"
                                                >
                                                    <Heart className="mr-2 h-4 w-4" />
                                                    {isSubmittingTip
                                                        ? 'Processing...'
                                                        : 'Continue to Payment'}
                                                </Button>
                                            </form>
                                        </div>
                                    </div>,
                                    document.body
                                )}
                            
                            {/* Tip Payment Modal */}
                            {showTipPaymentModal && artist && (
                                <Dialog open={showTipPaymentModal} onOpenChange={setShowTipPaymentModal}>
                                    <DialogContent className="bg-[#050816] border-blue-300/20 text-white max-w-md mx-auto max-h-[90vh] overflow-y-auto rounded-[2rem] shadow-[0_24px_90px_rgba(37,99,235,0.24)]">
                                        <DialogHeader>
                                            <DialogTitle className="flex items-center">
                                                <CreditCard className="w-5 h-5 mr-2" />
                                                {usePayPal
                                                    ? 'Send a tip with PayPal'
                                                    : 'Complete Tip Payment'}
                                            </DialogTitle>
                                            <DialogDescription>
                                                {usePayPal ? (
                                                    <>
                                                        {formatArtistCurrency(parseFloat(tipFormData.amount || 0))} tip for {artist.artist_name}, securely via PayPal
                                                    </>
                                                ) : (
                                                    <>
                                                        Pay {formatArtistCurrency(parseFloat(tipFormData.amount || 0))} to tip{' '}
                                                        {artist.artist_name}
                                                    </>
                                                )}
                                            </DialogDescription>
                                        </DialogHeader>
                                        
                                        {usePayPal ? (
                                            <Suspense
                                                fallback={
                                                    <div className="flex items-center justify-center gap-2 py-8 text-gray-400">
                                                        <Loader2 className="w-5 h-5 animate-spin" />
                                                        Loading checkout…
                                                    </div>
                                                }
                                            >
                                                <PayPalUSProcessor
                                                    artistCountry={artist.country}
                                                    artistId={artist.id}
                                                    amount={parseFloat(tipFormData.amount || 0)}
                                                    description={`Tip for ${artist.artist_name}${tipFormData.note ? `: ${tipFormData.note}` : ''}`}
                                                    onSuccess={handleTipPaymentSuccess}
                                                    onCancel={handleTipPaymentCancel}
                                                    mode="artist"
                                                    requestData={{
                                                        requesterName: tipFormData.name || 'Anonymous',
                                                        note: tipFormData.note || '',
                                                        tipAmount: parseFloat(tipFormData.amount || 0),
                                                    }}
                                                />
                                            </Suspense>
                                        ) : (
                                            <A2BPaymentForm
                                                amount={parseFloat(tipFormData.amount || 0)}
                                                artistId={artist.id}
                                                description={`Tip for ${artist.artist_name}${tipFormData.note ? `: ${tipFormData.note}` : ''}`}
                                                onSuccess={handleTipPaymentSuccess}
                                                onCancel={handleTipPaymentCancel}
                                                mode="artist"
                                                currency={artistCurrency}
                                                requestData={{
                                                    requesterName: tipFormData.name || 'Anonymous',
                                                    note: tipFormData.note || '',
                                                    tipAmount: parseFloat(tipFormData.amount || 0),
                                                }}
                                            />
                                        )}
                                    </DialogContent>
                                </Dialog>
                            )}


                            {/* Footer */}
                            <div className={`px-4 py-6 text-center ${basketSongs.length > 0 ? 'pb-28' : ''}`}>
                                <p className="text-gray-500 text-xs mb-1">
                                    Brought to you by:
                                </p>
                                <a 
                                    href="https://yoursongrequest.com" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-400 hover:text-blue-300 transition-colors bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent font-medium text-xs"
                                >
                                    YourSongRequest.com
                                </a>
                            </div>
                        </>
                    );
                } catch (error) {
                    console.error("Error rendering ArtistPage:", error);
                    return <div>An unexpected error occurred.</div>;
                }
            })()}
            {liveStatus?.is_live && basketSongs.length > 0 && !basketCheckoutOpen && (
                <RequestBasketBar
                    count={basketSongs.length}
                    totalLabel={formatArtistCurrency(basketTotal)}
                    onOpen={() => setBasketCheckoutOpen(true)}
                />
            )}
            <RequestBasketCheckout
                open={basketCheckoutOpen}
                onClose={() => setBasketCheckoutOpen(false)}
                songs={basketSongs}
                onRemoveSong={(songId) =>
                    setBasketSongs((prev) => prev.filter((item) => item.id !== songId))
                }
                artistId={artist.id}
                sessionId={sessionId}
                artistCurrency={artistCurrency}
                artistCountry={artist?.country}
                format={formatArtistCurrency}
                onPaid={handleBasketPaid}
            />
        </div>
    );
};

export default ArtistPage;